import I18nHelper from "@helpers/I18nHelper";
import LolRegionHelper from "@helpers/regions/LolRegionHelper";
import {
  ELoadingOptions,
  removeLoader,
  setLoader,
} from "@modules/loader/useLoader.hook";
import { getSelectedLanguage } from "@store/language.store";
import useLanguageSwitcherStore from "@store/languageSwitcher.store";
import { getSelectedRegion } from "@store/region.store";
import { forceLogoutUser, getUserToken } from "@store/user.store";
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  HttpStatusCode,
} from "axios";
import EStatus from "./models/EStatus";
import type { IResponse } from "./models/IResponse";

const envVars = import.meta.env;

interface ISearchParams {
  [key: string]: string | number | string[] | number[];
}

export class APIFetcher {
  private url: string;
  private token: string;

  constructor(url: string, token: string) {
    this.url = url;
    this.token = token;
  }

  public async makeRequest<T>(
    options: AxiosRequestConfig,
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
  ): Promise<IResponse<T>> {
    setLoader(loadingType);

    try {
      const response = await axios(options);

      return this.formatResponse<T>(response, false);
    } catch (error) {
      return this.handleError<T>(error as AxiosError<{ message?: string }>);
    } finally {
      removeLoader(loadingType);
    }
  }

  public async get<T>(
    uri: string,
    params: ISearchParams = {},
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
    headers: object = {},
    isAuth = true,
  ): Promise<IResponse<T>> {
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, params),
      method: "get",
      headers: this.getFinalHeaders(headers, isAuth),
    };

    return this.makeRequest<T>(options, loadingType);
  }

  public async post<T>(
    uri: string,
    body: object | string = {},
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
    headers: object = {},
    isAuth = true,
    isUrlEncoded = false,
  ): Promise<IResponse<T>> {
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "post",
      headers: this.getFinalHeaders(headers, isAuth),
      data:
        isUrlEncoded && typeof body === "object"
          ? this.buildURLEncodedData(body)
          : body,
    };

    return this.makeRequest<T>(options, loadingType);
  }

  public async patch<T>(
    uri: string,
    body: object = {},
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
    headers: object = {},
    isAuth = true,
    isUrlEncoded = false,
  ): Promise<IResponse<T>> {
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "patch",
      headers: this.getFinalHeaders(headers, isAuth),
      data: isUrlEncoded ? this.buildURLEncodedData(body) : body,
    };

    return this.makeRequest<T>(options, loadingType);
  }

  public async put<T>(
    uri: string,
    body: object = {},
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
    headers: object = {},
    isAuth = true,
    isUrlEncoded = false,
  ): Promise<IResponse<T>> {
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, {}),
      method: "put",
      headers: this.getFinalHeaders(headers, isAuth),
      data: isUrlEncoded ? this.buildURLEncodedData(body) : body,
    };

    return this.makeRequest<T>(options, loadingType);
  }

  public async delete<T>(
    uri: string,
    params: ISearchParams = {},
    loadingType: ELoadingOptions = ELoadingOptions.SKELETON,
    headers: object = {},
    isAuth = true,
  ): Promise<IResponse<T>> {
    const options: AxiosRequestConfig = {
      url: this.buildURL(uri, params),
      method: "delete",
      headers: this.getFinalHeaders(headers, isAuth),
    };

    return this.makeRequest<T>(options, loadingType);
  }

  private getFinalHeaders(headers: object, isAuth: boolean): object {
    const urlLanguage = I18nHelper.getLangFromPath(window.location.pathname);
    const storeLanguage = getSelectedLanguage();
    const region = getSelectedRegion();

    const finalHeaders: Record<string, string> = {
      language: urlLanguage ?? storeLanguage ?? I18nHelper.DEFAULT_LANGUAGE,
      region: region ?? LolRegionHelper.DEFAULT_REGION,
    };

    const token = getUserToken();
    const hasAuth = isAuth && token !== null;

    if (hasAuth) {
      finalHeaders.Authorization = `Bearer ${token}`;
    }

    return { ...finalHeaders, ...headers };
  }

  private buildURL(pathname: string, search: ISearchParams) {
    return this.url + pathname + this.buildURLParamsFromObject(search);
  }

  private isRequestSuccess = (code: number): boolean => {
    return code >= 200 && code < 300;
  };

  private formatResponse<T>(
    axiosResponse: AxiosResponse<T, T>,
    noMessage = false,
  ): IResponse<T> {
    let data: T;
    if (
      Object.hasOwn(axiosResponse, "data") &&
      !Object.hasOwn(axiosResponse.data as { data: T }, "meta") &&
      Object.hasOwn(axiosResponse.data as { data: T }, "data")
    ) {
      data = (axiosResponse.data as { data: T }).data;
    } else {
      data = axiosResponse.data;
    }

    if (data) {
      useLanguageSwitcherStore.getState().syncSlugsFromData(data);
    }

    if (noMessage) {
      return {
        ok: this.isRequestSuccess(axiosResponse.status),
        code: axiosResponse.status,
        data,
      };
    }

    return {
      ok: this.isRequestSuccess(axiosResponse.status),
      message: (axiosResponse.data as { message: string }).message,
      code: axiosResponse.status,
      data,
    };
  }

  private buildURLParamsFromObject(params: ISearchParams): string {
    const arrayParams: string[][] = [];
    if (Object.keys(params).length > 0) {
      for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
          for (const element of value) {
            arrayParams.push([key, element.toString()]);
          }
        } else {
          arrayParams.push([key, value.toString()]);
        }
      }
      return `?${new URLSearchParams(arrayParams).toString()}`;
    }

    const urlParams = new URLSearchParams(arrayParams);

    return urlParams.toString();
  }

  private buildURLEncodedData = (data: object) => {
    return new URLSearchParams(
      Object.entries(data).map(([key, value]) => [key ?? "", value ?? ""]),
    ).toString();
  };

  private handleError<T>(
    error: AxiosError<{ message?: string }>,
  ): Promise<IResponse<T>> {
    const message = error?.response?.data?.message;
    const status = error?.response?.status;

    if (status === EStatus.UNAUTHORIZED) forceLogoutUser();

    if (error?.response)
      return Promise.reject({
        message,
        ok: false,
        code: error?.response?.status,
        data: error?.response?.data,
      });
    return Promise.reject({
      message: error?.message,
      ok: false,
      code: HttpStatusCode.InternalServerError,
      data: error?.response?.data,
    });
  }
}

const API = new APIFetcher(
  `${envVars.VITE_API_HOST}${envVars.VITE_API_GROUP_ROUTES}`,
  "",
);

export default API;
